import * as React from "react";
import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface User {
  id: string;
  username: string | null;
}

export interface MentionTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  onMention?: (userId: string, username: string) => void;
}

const MentionTextarea = React.forwardRef<HTMLTextAreaElement, MentionTextareaProps>(
  ({ className, value, onChange, onMention, ...props }, ref) => {
    const [users, setUsers] = useState<User[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
    const [showMentions, setShowMentions] = useState(false);
    const [mentionQuery, setMentionQuery] = useState("");
    const [cursorPosition, setCursorPosition] = useState(0);
    const [mentionStartIndex, setMentionStartIndex] = useState(-1);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    useEffect(() => {
      loadUsers();
    }, []);

    const loadUsers = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from("profiles")
        .select("id, username")
        .neq("id", user.id);

      if (data) {
        setUsers(data);
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      const cursorPos = e.target.selectionStart || 0;
      
      onChange?.(e);
      setCursorPosition(cursorPos);

      // Check if @ was typed
      const textBeforeCursor = newValue.substring(0, cursorPos);
      const lastAtIndex = textBeforeCursor.lastIndexOf("@");
      
      if (lastAtIndex !== -1) {
        const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
        const hasSpace = textAfterAt.includes(" ");
        
        if (!hasSpace) {
          setMentionStartIndex(lastAtIndex);
          setMentionQuery(textAfterAt);
          
          // Filter users based on query
          const filtered = users.filter(user => 
            user.username?.toLowerCase().includes(textAfterAt.toLowerCase())
          );
          setFilteredUsers(filtered);
          setShowMentions(filtered.length > 0);
        } else {
          setShowMentions(false);
        }
      } else {
        setShowMentions(false);
      }
    };

    const insertMention = (user: User) => {
      if (!textareaRef.current || mentionStartIndex === -1) return;

      const currentValue = (value as string) || "";
      const beforeMention = currentValue.substring(0, mentionStartIndex);
      const afterCursor = currentValue.substring(cursorPosition);
      const mentionText = `@${user.username}`;
      const newValue = beforeMention + mentionText + " " + afterCursor;

      // Create synthetic event
      const syntheticEvent = {
        target: { value: newValue },
        currentTarget: { value: newValue }
      } as React.ChangeEvent<HTMLTextAreaElement>;

      onChange?.(syntheticEvent);
      onMention?.(user.id, user.username || "");
      
      setShowMentions(false);
      setMentionQuery("");
      setMentionStartIndex(-1);

      // Set cursor position after mention
      setTimeout(() => {
        if (textareaRef.current) {
          const newCursorPos = beforeMention.length + mentionText.length + 1;
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (showMentions && (e.key === "Escape")) {
        setShowMentions(false);
        e.preventDefault();
      }
      props.onKeyDown?.(e);
    };

    return (
      <Popover open={showMentions} onOpenChange={setShowMentions}>
        <PopoverTrigger asChild>
          <textarea
            className={cn(
              "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
              className
            )}
            ref={(node) => {
              textareaRef.current = node;
              if (typeof ref === "function") {
                ref(node);
              } else if (ref) {
                ref.current = node;
              }
            }}
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            {...props}
          />
        </PopoverTrigger>
        <PopoverContent 
          className="w-64 p-0" 
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command>
            <CommandList>
              <CommandEmpty>No users found</CommandEmpty>
              <CommandGroup heading="Mention User">
                {filteredUsers.map((user) => (
                  <CommandItem
                    key={user.id}
                    onSelect={() => insertMention(user)}
                    className="cursor-pointer"
                  >
                    <span className="font-medium">@{user.username || "Unknown"}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }
);

MentionTextarea.displayName = "MentionTextarea";

export { MentionTextarea };
